'use client';
import {
  BundleCartItem,
  BundleItem,
  BundleItemOption,
  BundleProduct,
  LineItem,
  ProductInterface,
  SelectedBundleOptionValue,
} from '@storefront/data-commerce';
import { NamespacesI18n } from '@storefront/i18n';
import {
  getPricesFromCart,
  getRegularPrice,
} from '@storefront/utils/priceHandler';
import {
  getLabelFromBundleCartItem,
  getMonthlyPages,
  getProductTypeLabelFromBundleCartItem,
  getProductTypeLabelFromItem,
  getProductTypeLabelFromOption,
  getSkuFromBundleCartItem,
  getTitleFromBundleItem,
} from '@storefront/utils/prodRequests';
import { getI18n } from 'react-i18next';

export type BundleCartItemWithProduct = BundleCartItem & {
  product: BundleProduct;
};

function getItemsMap(
  items: BundleCartItemWithProduct[]
): Map<string, BundleCartItemWithProduct> {
  const mapped = items.reduce(
    (acc, item) =>
      acc.set(
        (item.product as BundleProduct).items[0].options[0].product
          .hp_product_type_label,
        item
      ),
    new Map<string, BundleCartItemWithProduct>()
  );
  return mapped;
}

export class LineItemAdapter {
  private readonly itemsMap: Map<string, BundleCartItemWithProduct>;
  private readonly moreProducts: BundleCartItemWithProduct[];

  constructor(readonly items: BundleCartItemWithProduct[]) {
    this.items = items.filter((item) => item?.bundle_options);
    this.moreProducts = items.filter((item) => !item?.bundle_options);
    this.itemsMap = getItemsMap(this.items);
  }
  toLineItems(): LineItem[] {
    /// will skip if hybrid
    const filteredItems: BundleCartItemWithProduct[] = this.items.filter(
      this.shouldSkipItem.bind(this)
    );
    const lineItems = filteredItems.map((item: BundleCartItemWithProduct) =>
      this.getLineItems(item)
    );
    return lineItems.flat();
  }

  firstProductEntry(
    item: BundleCartItemWithProduct,
    cartItem: BundleCartItemWithProduct,
    firstProductType: string
  ): LineItem {
    const productSku = getSkuFromBundleCartItem(item);
    return {
      ...this.getProductItem(cartItem),
      label: firstProductType,
      name: getLabelFromBundleCartItem(item),
      sku: productSku,
    } as LineItem;
  }

  otherEntries(
    bundleItem: BundleItem,
    cartItem: BundleCartItemWithProduct,
    firstProductType: string,
    cartItemType: string
  ) {
    if (
      firstProductType === 'printer' &&
      cartItemType === 'printer' &&
      !bundleItem.hidden &&
      getProductTypeLabelFromItem(bundleItem) === 'instantInk'
    ) {
      const selectedOption = this.getSelectedItemOption(cartItem, bundleItem);
      return {
        ...this.getInstantInkItem(selectedOption),
        sku: selectedOption.product.sku,
        label: getProductTypeLabelFromOption(selectedOption),
        name: getProductTypeLabelFromItem(bundleItem),
      } as LineItem;
    } else if (
      getProductTypeLabelFromItem(bundleItem) === 'instantPaper' &&
      this.isPaperPresentOnBundle(cartItem)
    ) {
      const selectedOption = this.getSelectedItemOption(cartItem, bundleItem);
      return {
        ...this.getInstantPaperItem(selectedOption),
        sku: selectedOption.product.sku,
        label: getProductTypeLabelFromOption(selectedOption),
        name: getProductTypeLabelFromItem(bundleItem),
      } as LineItem;
    }
  }

  moreProductsEntries(moreProduct: BundleCartItemWithProduct) {
    return {
      ...this.getInstantPaperItemAsProduct(moreProduct),
      sku: moreProduct.product.sku,
      price: moreProduct.prices.price?.value,
      label: getProductTypeLabelFromOption(moreProduct),
      name: getProductTypeLabelFromOption(moreProduct),
    } as LineItem;
  }

  getLineItems(item: BundleCartItemWithProduct): LineItem[] {
    const lineItems: LineItem[] = [];

    const firstProductType = getProductTypeLabelFromBundleCartItem(item);

    for (const [cartItemType, cartItem] of this.itemsMap.entries()) {
      lineItems.push(this.firstProductEntry(item, cartItem, firstProductType));
      for (const bundleItem of cartItem.product.items) {
        const entry = this.otherEntries(
          bundleItem,
          cartItem,
          firstProductType,
          cartItemType
        );
        if (entry) lineItems.push(entry);
      }
    }
    if (this.moreProducts?.length !== 0) {
      for (const moreProduct of this.moreProducts) {
        const entry = this.moreProductsEntries(moreProduct);
        lineItems.push(entry);
      }
    }
    return lineItems;
  }

  private readonly isPaperPresentOnBundle = (
    bundleItem: BundleCartItem
  ): boolean =>
    bundleItem?.bundle_options?.some(
      (option) => option.label === 'Instant Paper'
    );

  private getProductItem(cartItem: BundleCartItemWithProduct): LineItem {
    const selectedItem = cartItem?.product?.items[0]?.options?.find(
      (option) => option.product.sku === getSkuFromBundleCartItem(cartItem)
    );
    return {
      title:
        getTitleFromBundleItem(cartItem?.product?.items?.[0]) ||
        selectedItem.label,
      thumbnail: [
        {
          src: selectedItem?.product?.thumbnail?.url,
        },
      ],
    };
  }

  getThumbnailUrlWithFallback = (product: ProductInterface): string => {
    if (product?.thumbnail?.url) return product.thumbnail.url;
    return product?.image?.url || product?.small_image?.url;
  };

  getInstantInkItem(selectedPlan: BundleItemOption): LineItem {
    const i18n = getI18n();
    const imageUrl = this.getThumbnailUrlWithFallback(selectedPlan?.product);
    const title = i18n.t('checkout.items.instantInk.monthlyPages', {
      ns: NamespacesI18n.CHECKOUT,
      pagesQty: getMonthlyPages(selectedPlan),
    });
    return {
      title: title,
      thumbnail: [{ src: imageUrl }],
      price: getRegularPrice(selectedPlan),
    };
  }
  getInstantPaperItemAsProduct(
    moreProduct: BundleCartItemWithProduct
  ): LineItem {
    const i18n = getI18n();
    const imageUrl = this.getThumbnailUrlWithFallback(moreProduct?.product);
    const title = i18n.t('checkout.items.instantPaper.paper', {
      ns: NamespacesI18n.CHECKOUT,
    });
    return {
      title: title,
      thumbnail: [{ src: imageUrl }],
      price: getPricesFromCart(moreProduct),
    };
  }
  getInstantPaperItem(selectedPlan: BundleItemOption): LineItem {
    const i18n = getI18n();
    const imageUrl = this.getThumbnailUrlWithFallback(selectedPlan?.product);
    const title = i18n.t('checkout.items.instantPaper.paper', {
      ns: NamespacesI18n.CHECKOUT,
    });
    return {
      title: title,
      thumbnail: [{ src: imageUrl }],
      price: getRegularPrice(selectedPlan),
    };
  }

  private getSelectedItemOption(
    cartItem: BundleCartItemWithProduct,
    bundleItem: BundleItem
  ): BundleItemOption {
    const selectedOptionsSkuList = cartItem.bundle_options.map(
      (option) => option.values[0].sku
    );
    return bundleItem.options.find((plan) =>
      selectedOptionsSkuList.includes(plan.product.sku)
    );
  }

  public toAllSelectedItems(): LineItem[] {
    const selectedItems: LineItem[][] = this.items.map(
      (item: BundleCartItemWithProduct) => this.getAllSelectedItems(item)
    );
    return selectedItems.flat();
  }

  private getAllSelectedItems(item: BundleCartItemWithProduct): LineItem[] {
    return item.bundle_options.flatMap((bundleOption) =>
      bundleOption.values.map((value) =>
        this.createLineItemFromValue(item, value)
      )
    );
  }

  private createLineItemFromValue(
    item: BundleCartItemWithProduct,
    value: SelectedBundleOptionValue
  ): LineItem & { category: string } {
    const isInstantInk = this.isInstantInkProduct(item, value.sku);

    return {
      sku: value.sku,
      name: isInstantInk ? `${value.label} - Instant Ink` : value.label,
      price: value.price,
      quantity: value.quantity,
      category: '/service/HPOne/',
    };
  }

  private isInstantInkProduct(
    item: BundleCartItemWithProduct,
    sku: string
  ): boolean {
    return item.product.items.some((item) =>
      item.options.some(
        (option) =>
          option.product.sku === sku &&
          getProductTypeLabelFromOption(option) === 'instantInk'
      )
    );
  }

  private isHybrid(itemsMap: Map<string, BundleCartItemWithProduct>): boolean {
    return (
      (itemsMap.has('pc') || itemsMap.has('chromebook')) &&
      itemsMap.has('printer')
    );
  }

  private shouldSkipItem(item: BundleCartItemWithProduct) {
    if (getProductTypeLabelFromItem(item?.product?.items?.[0]) === 'printer')
      return !this.isHybrid(this.itemsMap);
    return true;
  }
}
